import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Play, Crown } from "lucide-react";
import { sfx } from "@/lib/sfx";
import { useT } from "@/i18n";
import ReplayViewerDialog from "@/components/ReplayViewerDialog";
import { fetchTopGhost } from "@/lib/replay";
import { fetchWeeklyChampion, isChampionName } from "@/lib/champion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard() {
  const { t } = useT();
  const [tab, setTab] = useState("daily");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topGhost, setTopGhost] = useState(null);
  const [replayOpen, setReplayOpen] = useState(false);
  const [champion, setChampion] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    // Prefetch today's top ghost for the "WATCH" button on the daily tab.
    fetchTopGhost().then((g) => {
      if (g && g.replay && g.replay.length > 0) setTopGhost(g);
    });
    // Prefetch weekly champion for the crown badge.
    fetchWeeklyChampion().then((c) => {
      if (c && c.player_name) setChampion(c);
    });
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/leaderboard?level=${tab}&limit=10`);
        setRows(res.data || []);
      } catch (e) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tab]);

  return (
    <div className="min-h-screen w-full grid-bg py-10 px-6 md:px-16" data-testid="leaderboard-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-4">
          <button
            data-testid="back-to-menu"
            onClick={() => {
              sfx.click();
              nav("/");
            }}
            className="flex items-center gap-2 text-xs tracking-[0.3em] text-white/60 hover:text-cyan-300 transition-colors mb-8"
          >
            <ChevronLeft size={14} /> {t("leaderboard.back")}
          </button>
          <button
            data-testid="hall-of-champions-link"
            onClick={() => { sfx.click(); nav("/champions"); }}
            className="flex items-center gap-2 mb-8 px-3 py-1.5 border border-[#FFD400]/50 text-[#FFD400] text-[10px] tracking-[0.3em] hover:bg-[#FFD400] hover:text-black transition-colors"
            style={{ boxShadow: "0 0 14px rgba(255,212,0,0.25)" }}
          >
            <Crown size={12} /> {t("leaderboard.hall_of_champions") || "HALL OF CHAMPIONS"}
          </button>
        </div>

        <div className="flex items-center gap-3 text-[10px] tracking-[0.4em] text-white/60">
          <span className="w-8 h-px bg-cyan-300" /> {t("leaderboard.header")}
        </div>
        <h1 className="mt-3 font-display font-black text-5xl md:text-7xl tracking-tighter">
          {t("leaderboard.title_lead")}<span className="text-cyan-300 glow-cyan">{t("leaderboard.title_accent")}</span>
        </h1>

        <div className="mt-8 flex gap-2 flex-wrap">
          <TabBtn active={tab === "daily"} onClick={() => setTab("daily")} testid="tab-daily" color="#FFD400">
            {t("leaderboard.tab_daily") || "DAILY"}
          </TabBtn>
          <TabBtn active={tab === "weekly"} onClick={() => setTab("weekly")} testid="tab-weekly" color="#FF9020">
            {t("leaderboard.tab_weekly") || "WEEKLY"}
          </TabBtn>
          <TabBtn active={tab === "outdoor"} onClick={() => setTab("outdoor")} testid="tab-outdoor" color="#00F0FF">
            {t("leaderboard.tab_outdoor")}
          </TabBtn>
          <TabBtn active={tab === "underground"} onClick={() => setTab("underground")} testid="tab-underground" color="#FF0055">
            {t("leaderboard.tab_underground")}
          </TabBtn>
        </div>

        <div className="mt-6 border border-white/10 bg-black/40 backdrop-blur-md">
          {tab === "daily" && topGhost && (
            <div
              className="flex items-center justify-between px-6 py-3 border-b border-white/10 bg-black/40"
              data-testid="watch-ghost-cta"
            >
              <div className="text-[10px] tracking-[0.3em] text-white/60 flex items-center gap-2">
                TODAY'S #1 · <span className="text-[#FFD400]">{topGhost.player_name}</span>
                {isChampionName(champion, topGhost.player_name) && (
                  <Crown size={12} className="text-[#FFD400]" style={{ filter: "drop-shadow(0 0 6px rgba(255,212,0,0.7))" }} />
                )}
                · {String(topGhost.score).padStart(6, "0")}
              </div>
              <button
                data-testid="watch-ghost-button"
                onClick={() => { sfx.click(); setReplayOpen(true); }}
                className="flex items-center gap-2 px-4 py-2 border border-[#FFD400] text-[#FFD400] text-[10px] tracking-[0.3em] hover:bg-[#FFD400] hover:text-black transition-colors"
                style={{ boxShadow: "0 0 16px rgba(255,212,0,0.35)" }}
              >
                <Play size={12} /> {t("leaderboard.watch_ghost") || "WATCH GHOST"}
              </button>
            </div>
          )}
          <div className="grid grid-cols-12 text-[10px] tracking-[0.3em] text-white/50 px-6 py-3 border-b border-white/10">
            <div className="col-span-1">{t("leaderboard.col_rank")}</div>
            <div className="col-span-5">{t("leaderboard.col_name")}</div>
            <div className="col-span-3 text-right">{t("leaderboard.col_time")}</div>
            <div className="col-span-3 text-right">{t("leaderboard.col_score")}</div>
          </div>
          {loading && (
            <div className="p-8 text-center text-white/50 text-xs tracking-[0.3em]" data-testid="leaderboard-loading">
              {t("leaderboard.loading")}
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-8 text-center text-white/50 text-xs tracking-[0.3em]" data-testid="leaderboard-empty">
              {t("leaderboard.empty")}
            </div>
          )}
          {!loading &&
            rows.map((r, i) => (
              <div
                key={r.id || i}
                data-testid={`leaderboard-row-${i}`}
                className={`grid grid-cols-12 items-center px-6 py-3 text-sm border-b border-white/5 ${i === 0 ? "text-[#FFD400]" : "text-white/85"}`}
                style={i === 0 ? { textShadow: "0 0 14px rgba(255,212,0,0.4)" } : {}}
              >
                <div className="col-span-1 font-bold tabular-nums">{String(i + 1).padStart(2, "0")}</div>
                <div className="col-span-5 uppercase tracking-widest truncate flex items-center gap-2">
                  {r.player_name}
                  {isChampionName(champion, r.player_name) && (
                    <Crown
                      size={12}
                      className="text-[#FFD400] shrink-0"
                      style={{ filter: "drop-shadow(0 0 6px rgba(255,212,0,0.7))" }}
                      data-testid="champion-crown"
                    />
                  )}
                </div>
                <div className="col-span-3 text-right tabular-nums">{(r.survival_time || 0).toFixed(1)}s</div>
                <div className="col-span-3 text-right tabular-nums font-bold">{String(r.score).padStart(6, "0")}</div>
              </div>
            ))}
        </div>
      </div>
      {topGhost && (
        <ReplayViewerDialog
          open={replayOpen}
          onOpenChange={setReplayOpen}
          playerName={topGhost.player_name}
          score={topGhost.score}
          replay={topGhost.replay}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid, color }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="px-5 py-2 text-xs tracking-[0.3em] border transition-colors"
      style={{
        borderColor: active ? color : "rgba(255,255,255,0.15)",
        color: active ? color : "rgba(255,255,255,0.6)",
        background: active ? "rgba(0,0,0,0.6)" : "transparent",
        boxShadow: active ? `0 0 20px ${color}55` : "none",
      }}
    >
      {children}
    </button>
  );
}
